<script setup>
import { useStore } from "vuex";
import { computed, onMounted, ref, watch } from "vue";
import { getImageInfo } from "../utils/preload-image";
import PreviewMessage from "./PreviewMessage.vue";
import { NECK_LINE_MODES } from "../misc/constants";

const props = defineProps({
  sideKey: {
    type: String,
    required: true
  }
});

const store = useStore();
const supportsMade = computed(() => store.getters["app/supportsMade"]);
const surfaceLabel = computed(() => store.getters[`surface_${props.sideKey}/surfaceLabel`]);
const downscaleFactor = computed(() => store.getters[`surface_${props.sideKey}/downscaleFactor`]);
const artworkSize = computed(() => store.getters[`artwork_${props.sideKey}/artworkSize`]);
const artworkUrl = computed(() => store.getters[`artwork_${props.sideKey}/artworkUrl`]);
const surface = computed(() => store.getters[`surface_${props.sideKey}/surface`]);
const printableAreas = computed(() => store.getters["creation/printableAreas"]);
const artworkExists = computed(() => store.getters[`artwork_${props.sideKey}/artworkExists`]);
const shownCustomMessage = computed(() => {
  const neckAvailable = store.getters.config.made_custom_neck_distances;
  const neckLines = store.getters["product/neckLines"][surfaceLabel.value];
  const neckLineMode = store.getters[`surface_${props.sideKey}/neckLineMode`];
  return neckAvailable && neckLines && neckLineMode === NECK_LINE_MODES.CUSTOM;
});

const canvasPreview = ref(null);
const previewUrl = computed(() => {
  const productId = store.getters["product/productId"];
  const productArea = printableAreas.value.find(item => item.product_id === productId);
  const compositionId = store.getters[`product/compositionId`];
  const compositionArea = productArea?.compositions.find(item => item.id === compositionId);

  if (!compositionArea) return surface.value.picture;

  const [printableArea] = compositionArea.printable_areas[surfaceLabel.value];
  return canvasPreview.value || printableArea.image_url;
});

const createPreview = async area => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const {
    image: backdropImage,
    width,
    height
  } = await getImageInfo(area.backdropUrl, { crossOrigin: "anonymous" });
  const { image: artworkImage } = await getImageInfo(area.artworkUrl, { crossOrigin: "anonymous" });

  ctx.canvas.width = width;
  ctx.canvas.height = height;
  ctx.clearRect(0, 0, width, height); // clear canvas
  ctx.drawImage(backdropImage, 0, 0, width, height);

  const angle = (area.rotation * Math.PI) / 180;
  const [x, y] = area.center;
  ctx.save();
  ctx.translate(x * -angle, y * angle);
  ctx.rotate(-angle);
  ctx.drawImage(artworkImage, x - area.width / 2, y, area.width, area.height);
  ctx.restore();
  return canvas.toDataURL();
};

const resetPreview = () => {
  canvasPreview.value = null;
};

const initPreview = async () => {
  if (!artworkExists.value) {
    resetPreview();
    return;
  }

  const compositionArea = await store.dispatch("creation/fetchAreas");

  if (!compositionArea) {
    resetPreview();
    return;
  }

  const [area] = compositionArea.printable_areas[surfaceLabel.value];
  const width = (artworkSize.value.width / downscaleFactor.value / surface.value.ppi) * area.ppi;
  const height = (artworkSize.value.height / downscaleFactor.value / surface.value.ppi) * area.ppi;
  const payloadArea = {
    width,
    height,
    artworkUrl: artworkUrl.value,
    backdropUrl: area.image_url,
    rotation: area.rotation_angle,
    center: [area.x, area.y]
  };
  canvasPreview.value = await createPreview(payloadArea);
};

watch(
  [artworkSize, artworkUrl, surface, printableAreas, downscaleFactor, artworkExists],
  initPreview
);
onMounted(initPreview);
</script>

<template>
  <div
    class="creation-preview"
    :class="{ 'creation-preview--disabled': !artworkExists || !supportsMade }"
  >
    <div class="creation-preview__title">Your creation</div>

    <div class="creation-preview__preview-wrapper">
      <div v-if="shownCustomMessage" class="creation-preview__intro">
        <PreviewMessage />
      </div>

      <img alt="Product Preview Image" :src="previewUrl" class="creation-preview__image" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.creation-preview {
  font-family: $pod-font-family;

  &--disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  &__title {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 10px;
    color: $black;
  }

  &__intro {
    @include with-container(container, $pod-pdp-desktop-response) {
      position: absolute;
      top: 6px;
      left: 6px;
    }
  }

  &__preview-wrapper {
    @include with-container(container, $pod-pdp-desktop-response) {
      border-radius: 8px;
      border: 1px solid $pod-gray-modern-200;
      background-color: $white;
      box-shadow: $pod-card-shadow;
      padding-top: 34px;
      height: 625px;
      position: relative;
    }
  }

  &__image {
    display: block;
    max-width: 100%;

    @include with-container(container, $pod-pdp-desktop-response) {
      margin: 0 auto;
      max-height: 100%;
    }
  }
}
</style>
